import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { BrandIcon } from "@/components/BrandIcon";
import { EbaySetupGuide, EbayAccounts, EbaySettings } from "@/components/ebay/EbaySetup";
import { EbayListings } from "@/components/ebay/EbayListings";
import { EbayStockPreview } from "@/components/ebay/EbayStockPreview";

const TABS = [["setup", "Setup"], ["listings", "Listings"], ["stock", "Stock comparison"], ["settings", "Settings"]];

export default function EbayPage() {
  const { user, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [st, setSt] = useState(null);
  const [tab, setTab] = useState("setup");
  const [acc, setAcc] = useState("");
  const canManage = !!(user?.is_super || can("marketplace.manage"));
  const load = useCallback(() => {
    api.get("/ebay/status").then((r) => {
      setSt(r.data);
      setAcc((a) => a || r.data.accounts[0]?.id || "");
    }).catch((e) => setSt({ error: e?.response?.data?.detail || "Could not load eBay" }));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const c = params.get("connected");
    if (c) {
      if (c === "1") { toast.success("eBay account connected"); } else if (c === "taken") { toast.error("That eBay account is already connected to another company"); } else { toast.error("eBay connection was cancelled or failed"); }
      setParams({}, { replace: true });
    }
  }, [params, setParams]);
  if (!st) return null;
  if (st.error) return <div className="p-8 text-sm text-slate-500" data-testid="ebay-error">{st.error}</div>;
  return (
    <div className="p-4 sm:p-8 max-w-6xl space-y-5" data-testid="ebay-page">
      <Link to="/marketplace" className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-slate-800"><ArrowLeft className="w-3.5 h-3.5" />Marketplace</Link>
      <div className="flex flex-wrap items-center gap-3">
        <BrandIcon name="eBay" size={28} />
        <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900">eBay</h1>
        <span className="text-xs font-semibold rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-2.5 py-1" data-testid="ebay-readonly-badge">Read-only phase</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ebay-tab-${k}`}
            className={`text-sm rounded-lg px-3 py-1.5 transition-colors ${tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>))}
        {["listings", "stock"].includes(tab) && st.accounts.length > 1 && (
          <select value={acc} onChange={(e) => setAcc(e.target.value)} className="ml-auto h-9 rounded-lg border border-slate-200 px-2 text-sm" data-testid="ebay-account-select">
            {st.accounts.map((a) => <option key={a.id} value={a.id}>{a.label || a.ebay_username}</option>)}</select>)}
      </div>
      {tab === "setup" && <><EbayAccounts st={st} isSuper={!!user?.is_super} reload={load} /><EbaySetupGuide st={st} /></>}
      {tab === "settings" && <EbaySettings st={st} reload={load} canManage={canManage} />}
      {["listings", "stock"].includes(tab) && (!acc ? <p className="text-sm text-slate-400" data-testid="ebay-no-account">Connect an eBay account first.</p>
        : tab === "listings" ? <EbayListings key={acc} accountId={acc} canManage={canManage} /> : <EbayStockPreview key={acc} accountId={acc} canManage={canManage} />)}
    </div>
  );
}
